/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   StorageInsightsPage.tsx
 *     Feature         :   217-insights-dashboards (T024 / US4, FR-004) — reworked
 *     Description     :   Storage Insights — meaningful storage analytics that replace the
 *                         quota-centric content (quota is never collected). Shows where the
 *                         bytes live (region), durability posture (replication), security
 *                         posture (public-blob / legacy-TLS / open-network / legacy-kind /
 *                         not-geo-redundant), fleet growth, and the largest / riskiest /
 *                         backup-holding accounts — with subscription attribution. Every KPI
 *                         carries an Info tooltip naming its number + source; anything not
 *                         collected renders NotCollected with a real reason (FR-014).
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { useCanViewCost } from '../../hooks/useCanViewCost';
import { HardDrive, Info, ShieldAlert, TrendingDown } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from 'recharts';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout, Card, DataTable, StatCard, AppTooltip, Badge, ErrorState, LoadingState, NotCollected,
  Extended, QuickGlance, chartTooltipStyle, CostSourceBadge, formatUsd,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

const REGION_COLORS = ['#3b82f6', '#8b5cf6', '#06b6d4', '#f59e0b', '#10b981', '#ec4899', '#ef4444', '#14b8a6'];
const REPL_COLORS = ['#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#f59e0b'];

interface RegionRow { region: string; usedGb: number; accounts: number }
interface ReplicationRow { replication: string; accounts: number; usedGb: number }
interface PostureRow { label: string; count: number; detail: string }
interface LargestAccountRow {
  accountId: string; name: string; subscriptionName: string | null;
  region: string | null; replication: string; accessTier: string | null; usedGb: number;
}
interface SecurityRiskAccountRow {
  accountId: string; name: string; subscriptionName: string | null; region: string | null; issues: string[];
}
interface BackupHoldingAccountRow {
  accountId: string; name: string; subscriptionName: string | null;
  region: string | null; replication: string; usedGb: number;
}
interface HotToCoolCandidate {
  accountId: string; name: string; subscriptionName: string | null; region: string | null;
  replication: string; usedGb: number; currentTier: string; suggestedTier: string;
  monthlySavingUsd: number | null; rateEstimated: boolean;
}
interface ReplicationChangeRow {
  accountId: string; name: string; subscriptionName: string | null; region: string | null;
  currentReplication: string; targetReplication: string; usedGb: number;
  monthlyDeltaUsd: number | null; rateEstimated: boolean;
}
interface StorageOptimization {
  ratesAvailable: boolean;
  ratesAsOf: string | null;
  hotToCool: { candidates: HotToCoolCandidate[]; totalMonthlySavingUsd: number | null };
  overProvisioned: { rows: ReplicationChangeRow[]; totalMonthlySavingUsd: number | null };
  underProtected: { rows: ReplicationChangeRow[]; totalMonthlyUpliftUsd: number | null };
}

interface StorageInsightsPayload {
  kpis: {
    totalAccounts: number;
    totalManagedGb: number;
    fleetGb: number;
    backupGb: number;
    accountsWithData: number;
    securityRiskCount: number;
    geoRedundantCount: number;
    geoRedundantPct: number | null;   // null over no accounts (Spec 298 FR-004)
    actualMonthlyCostUsd: number | null;
    ratesAsOf: string | null;
    containerCount: number;
  };
  byRegion: RegionRow[];
  byReplication: ReplicationRow[];
  securityPosture: PostureRow[];
  fleetTrend: Array<{ date: string; usedGb: number }>;
  largestAccounts: LargestAccountRow[];
  securityRiskAccounts: SecurityRiskAccountRow[];
  backupHoldingAccounts: BackupHoldingAccountRow[];
  optimization: StorageOptimization;
}

/**
 * Plain-language explainer for the six Azure replication SKUs — used in the chart
 * InfoTip AND (mirrored) in the page help. Multipliers are data-at-rest price vs
 * LRS (LRS = 1.0×); geo options also incur inter-region egress.
 */
const REPLICATION_EXPLAINER =
  'Replication = how many copies Azure keeps and where. ' +
  'LRS (1.0× price): 3 copies in ONE datacenter — protects against disk/rack failure only. ' +
  'ZRS (~1.25×): 3 copies across availability zones in one region — survives a datacenter/zone outage. ' +
  'GRS (~2.0×): LRS + async copy to a paired region — survives a regional outage (secondary not readable). ' +
  'RA-GRS (~2.5×): GRS with read access to the secondary. ' +
  'GZRS (~2.5×): ZRS + geo copy. RA-GZRS (~3.1×): GZRS with read access to the secondary. ' +
  'Geo options also add inter-region egress. Higher tiers cost more but protect against bigger failures — match the SKU to how critical the data is.';

/** Format a $/month figure, or a dash when the rate is not collected (never a fake number). */
const fmtUsd = (n: number | null): string =>
  n == null ? '—' : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/** Account name + subscription subtitle cell. */
function accountCell(name: string, subscriptionName: string | null): React.ReactNode {
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-[var(--color-text-primary)]">{name}</div>
      <div className="truncate text-xs text-[var(--color-text-secondary)]">{subscriptionName ?? 'no subscription'}</div>
    </div>
  );
}

/** A tiny Info-icon tooltip that matches the house pattern on the tiles. */
function InfoTip({ title }: { title: string }): React.ReactElement {
  return (
    <AppTooltip title={title}>
      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
    </AppTooltip>
  );
}

/** StatCard with an Info tooltip sitting on the label. */
function TileWithInfo({
  label, value, tip, valueClassName,
}: { label: string; value: React.ReactNode; tip: string; valueClassName?: string }): React.ReactElement {
  return (
    <StatCard
      label={<span className="inline-flex items-center gap-1">{label}<InfoTip title={tip} /></span>}
      value={value}
      valueClassName={valueClassName}
    />
  );
}

const fmtGb = (n: number): string => `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;

export const StorageInsightsPage: React.FC = () => {
  const query = useQuery<StorageInsightsPayload>({
    queryKey: ['storage-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: StorageInsightsPayload }>('/api/storage-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);

  const largestColumns: DataTableColumn<LargestAccountRow>[] = [
    { key: 'name', header: 'Account', width: '34%', render: (r) => accountCell(r.name, r.subscriptionName) },
    { key: 'region', header: 'Region', width: '16%', render: (r) => r.region ?? '—' },
    { key: 'replication', header: 'Replication', width: '20%', render: (r) => r.replication },
    { key: 'accessTier', header: 'Tier', width: '12%', render: (r) => r.accessTier ?? '—' },
    { key: 'usedGb', header: 'Used', width: '18%', align: 'right', render: (r) => fmtGb(r.usedGb) },
  ];

  const riskColumns: DataTableColumn<SecurityRiskAccountRow>[] = [
    { key: 'name', header: 'Account', width: '34%', render: (r) => accountCell(r.name, r.subscriptionName) },
    { key: 'region', header: 'Region', width: '16%', render: (r) => r.region ?? '—' },
    {
      key: 'issues', header: 'Issues', width: '50%',
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.issues.map((issue) => (
            <Badge key={issue} variant={issue.startsWith('Public') || issue.startsWith('Legacy TLS') ? 'error' : 'warning'} size="xs">
              {issue}
            </Badge>
          ))}
        </div>
      ),
    },
  ];

  const backupColumns: DataTableColumn<BackupHoldingAccountRow>[] = [
    { key: 'name', header: 'Account', width: '38%', render: (r) => accountCell(r.name, r.subscriptionName) },
    { key: 'region', header: 'Region', width: '18%', render: (r) => r.region ?? '—' },
    { key: 'replication', header: 'Replication', width: '26%', render: (r) => r.replication },
    { key: 'usedGb', header: 'Used', width: '18%', align: 'right', render: (r) => fmtGb(r.usedGb) },
  ];

  const hotCandidateColumns: DataTableColumn<HotToCoolCandidate>[] = [
    { key: 'name', header: 'Account', width: '30%', render: (r) => accountCell(r.name, r.subscriptionName) },
    { key: 'replication', header: 'Repl.', width: '12%', render: (r) => r.replication },
    { key: 'usedGb', header: 'Used', width: '16%', align: 'right', render: (r) => fmtGb(r.usedGb) },
    {
      key: 'move', header: 'Suggested move', width: '20%',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 text-[var(--color-text-secondary)]">
          <Badge variant="warning" size="xs">{r.currentTier}</Badge>→<Badge variant="success" size="xs">{r.suggestedTier}</Badge>
        </span>
      ),
    },
    {
      key: 'saving', header: 'Est. saving / mo', width: '22%', align: 'right',
      render: (r) => (
        <span className="inline-flex items-center justify-end gap-1 font-medium text-[var(--color-status-success)]">
          {fmtUsd(r.monthlySavingUsd)}
          {r.rateEstimated && (
            <AppTooltip title="Cool rate for this replication SKU is not collected — estimated from the default $0.02/GB rate.">
              <span><Badge variant="secondary" size="xs">est.</Badge></span>
            </AppTooltip>
          )}
        </span>
      ),
    },
  ];

  const replChangeColumns = (deltaHeader: string, deltaClass: string): DataTableColumn<ReplicationChangeRow>[] => [
    { key: 'name', header: 'Account', width: '34%', render: (r) => accountCell(r.name, r.subscriptionName) },
    { key: 'usedGb', header: 'Used', width: '16%', align: 'right', render: (r) => fmtGb(r.usedGb) },
    {
      key: 'change', header: 'Replication', width: '26%',
      render: (r) => (
        <span className="inline-flex items-center gap-1.5 text-[var(--color-text-secondary)]">
          <Badge variant="secondary" size="xs">{r.currentReplication}</Badge>→<Badge variant="secondary" size="xs">{r.targetReplication}</Badge>
        </span>
      ),
    },
    {
      key: 'delta', header: deltaHeader, width: '24%', align: 'right',
      render: (r) => (
        <span className={`inline-flex items-center justify-end gap-1 font-medium ${deltaClass}`}>
          {fmtUsd(r.monthlyDeltaUsd)}
          {r.rateEstimated && (
            <AppTooltip title="Rate for this replication SKU / tier is not collected — estimated from a default rate.">
              <span><Badge variant="secondary" size="xs">est.</Badge></span>
            </AppTooltip>
          )}
        </span>
      ),
    },
  ];

  useAutoRefresh(refresh, { lastFetchedAt });

  // Owner directive: cost / spend figures are Admin + Manager only.
  const canViewCost = useCanViewCost();

  return (
    <PageLayout
      title="Storage Insights"
      description="Where the bytes live, how durable they are, and how safely they're configured"
      icon={<HardDrive className="h-6 w-6" />}
      helpId="insights.storage"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
    >
      {error && !data ? (
        <ErrorState title="Failed to load storage insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <div className={`mb-6 grid grid-cols-2 gap-4 ${canViewCost ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
            <TileWithInfo
              label="Storage Accounts"
              value={data.kpis.totalAccounts}
              tip={`Active Azure storage accounts collected into dbo.storage_accounts (is_active = 1). ${data.kpis.accountsWithData} have a measured non-zero footprint.`}
            />
            <TileWithInfo
              label="Managed (GB)"
              value={data.kpis.totalManagedGb.toLocaleString()}
              tip={`Total used bytes across fleet + backup accounts (vault-linked accounts excluded, never double-counted). Fleet ${data.kpis.fleetGb.toLocaleString()} GB · Backup ${data.kpis.backupGb.toLocaleString()} GB. Classified by backup binding, the same rule as the Admin footprint tile.`}
            />
            <TileWithInfo
              label="Security Risks"
              value={data.kpis.securityRiskCount}
              valueClassName={data.kpis.securityRiskCount > 0 ? 'text-[var(--color-status-error)]' : undefined}
              tip="Accounts with a genuine misconfiguration — anonymous public blob access enabled OR a TLS floor below 1.2 — from the posture columns on dbo.storage_accounts."
            />
            <TileWithInfo
              label="Geo-Redundant"
              value={data.kpis.geoRedundantPct === null ? 'no accounts' : `${data.kpis.geoRedundantCount} · ${data.kpis.geoRedundantPct}%`}
              tip="Accounts on a geo-redundant SKU (GRS/RA-GRS/GZRS) that survive a regional outage, from dbo.storage_accounts.is_geo_redundant. The rest are locally-redundant (LRS)."
            />
            {canViewCost && (
              <div>
                {data.kpis.actualMonthlyCostUsd == null ? (
                  <TileWithInfo
                    label="Actual Monthly Cost"
                    value="—"
                    tip="No Azure billing (dbo.azure_cost_daily) for storage accounts in the last 30 days — NotCollected, no cost fabricated."
                  />
                ) : (
                  <TileWithInfo
                    label="Actual Monthly Cost"
                    value={formatUsd(data.kpis.actualMonthlyCostUsd)}
                    tip="Real Azure billing (dbo.azure_cost_daily) for storage accounts over the trailing 30 days. Not an estimate."
                  />
                )}
                <div className="mt-1"><CostSourceBadge source="actual" window="Last 30 days" /></div>
              </div>
            )}
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for region, replication, and security-posture
              breakdowns, the fleet growth trend, and the largest / riskiest / backup-holding accounts.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="space-y-6">
              {/* Region + Replication */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Where the bytes live</h2>
                    <InfoTip title="Total used GB per Azure region, summed across accounts in that region (dbo.storage_accounts.used_gb grouped by region). Bar label shows account count." />
                  </div>
                  {data.byRegion.length === 0 ? (
                    <NotCollected reason="No storage accounts collected" />
                  ) : (
                    <div style={{ height: Math.max(200, data.byRegion.length * 30) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.byRegion} layout="vertical" margin={{ left: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} unit=" GB" />
                          <YAxis type="category" dataKey="region" tick={{ fontSize: 10 }} width={110} />
                          <Tooltip
                            {...chartTooltipStyle}
                            formatter={(v, _n, p: any) => [`${Number(v).toLocaleString()} GB · ${p?.payload?.accounts ?? 0} accts`, 'Used']}
                          />
                          <Bar dataKey="usedGb" name="Used (GB)">
                            {data.byRegion.map((_, i) => <Cell key={i} fill={REGION_COLORS[i % REGION_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Card>

                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Replication &amp; durability</h2>
                    <InfoTip title={`Account count by replication SKU (dbo.storage_accounts.sku). ${REPLICATION_EXPLAINER}`} />
                  </div>
                  {data.byReplication.length === 0 ? (
                    <NotCollected reason="No storage accounts collected" />
                  ) : (
                    <div style={{ height: Math.max(200, data.byReplication.length * 34) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.byReplication} layout="vertical" margin={{ left: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                          <YAxis type="category" dataKey="replication" tick={{ fontSize: 10 }} width={120} />
                          <Tooltip
                            {...chartTooltipStyle}
                            formatter={(v, _n, p: any) => [`${Number(v)} accounts · ${(p?.payload?.usedGb ?? 0).toLocaleString()} GB`, 'Accounts']}
                          />
                          <Bar dataKey="accounts" name="Accounts">
                            {data.byReplication.map((_, i) => <Cell key={i} fill={REPL_COLORS[i % REPL_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Card>
              </div>

              {/* Security posture + Fleet growth */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-[var(--color-status-warning)]" />
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Security posture</h2>
                    <InfoTip title="Number of accounts in each less-secure configuration state, from the posture columns on dbo.storage_accounts (public blob access, TLS floor, network default action, geo-redundancy, account kind). Fewer is better." />
                  </div>
                  {data.securityPosture.length === 0 ? (
                    <NotCollected reason="No posture flags set — every account is on the hardened configuration" />
                  ) : (
                    <div style={{ height: Math.max(200, data.securityPosture.length * 36) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.securityPosture} layout="vertical" margin={{ left: 20 }}>
                          <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                          <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} width={140} />
                          <Tooltip {...chartTooltipStyle} formatter={(v) => [`${Number(v)} accounts`, 'Affected']} />
                          <Bar dataKey="count" name="Accounts" fill="var(--color-status-warning)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </Card>

                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Fleet growth — 60 days</h2>
                    <InfoTip title="Total used GB across all active accounts by day, from dbo.storage_account_snapshots. Days where no account reported a reading are omitted (never fabricated as 0)." />
                  </div>
                  {data.fleetTrend.length > 1 ? (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data.fleetTrend}>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={44} />
                          <Tooltip {...chartTooltipStyle} formatter={(v) => [`${Number(v).toLocaleString()} GB`, 'Used']} />
                          <Line type="monotone" dataKey="usedGb" name="Used (GB)" stroke="var(--color-accent)" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <NotCollected reason="Fewer than 2 days of storage account snapshots collected in the 60-day window" />
                  )}
                </Card>
              </div>

              {/* Largest accounts */}
              <Card padding="none">
                <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Largest accounts by used storage</h2>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Where the footprint is concentrated · used GB from dbo.storage_accounts</p>
                <DataTable columns={largestColumns} rows={data.largestAccounts} rowKey={(r) => r.accountId} pageSize={10} emptyMessage="No accounts with a measured footprint" />
              </Card>

              {/* Security-risk accounts */}
              <Card padding="none">
                <div className="flex items-center gap-2 px-4 pt-4">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Security-risk accounts</h2>
                  <InfoTip title="Accounts with a genuine misconfiguration (public blob access or a TLS floor below 1.2). Each account's specific issues are listed; an open network default is shown as context where it applies." />
                </div>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Most issues first · remediate public blob access and legacy TLS</p>
                <DataTable columns={riskColumns} rows={data.securityRiskAccounts} rowKey={(r) => r.accountId} pageSize={10} emptyMessage="No accounts with public blob access or legacy TLS — the fleet is hardened" />
              </Card>

              {/* Backup-holding accounts */}
              <Card padding="none">
                <div className="flex items-center gap-2 px-4 pt-4">
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Backup-holding accounts</h2>
                  <InfoTip title="Accounts that store backups — classified by backup binding (any backup run, storage map, plan target, or copy config references the account), never by the unreliable storage_purpose column." />
                </div>
                <p className="px-4 text-xs text-[var(--color-text-secondary)]">Which accounts hold our backups · used GB from dbo.storage_accounts</p>
                <DataTable columns={backupColumns} rows={data.backupHoldingAccounts} rowKey={(r) => r.accountId} pageSize={10} emptyMessage="No accounts are classified as backup targets" />
              </Card>

              {/* ── Cost optimization (Admin/Manager only — owner directive) ─────── */}
              {canViewCost && (
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-[var(--color-status-success)]" />
                  <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Cost optimization</h2>
                  <CostSourceBadge source="modeled" />
                  <InfoTip title="Data-grounded savings from the collected storage rates (dbo.storage_cost_rates) applied to the entity-first fleet/backup classification. Heuristics, not guarantees — there is no per-blob last-access telemetry, so tier candidates are limited to accounts with backup-binding evidence." />
                </div>

                <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
                  <p>
                    <span className="font-medium text-[var(--color-text-primary)]">Heuristic, not a guarantee.</span>{' '}
                    No per-blob last-access data is collected, so tier candidates below are limited to accounts with
                    backup-binding evidence (backups are written once and read rarely). Cool and Archive tiers have higher
                    read / retrieval costs and a minimum-retention charge (Cool 30 days, Archive 180 days plus hours-long
                    rehydration) — move only data you are confident is cold. All figures use the collected rates
                    {data.optimization.ratesAsOf ? ` (as of ${data.optimization.ratesAsOf.slice(0, 10)})` : ''}; a row marked
                    “est.” had no collected rate for its exact SKU × tier and fell back to a default.
                  </p>
                  {!data.optimization.ratesAvailable && (
                    <p className="mt-2">
                      <NotCollected reason="No storage cost rates collected (dbo.storage_cost_rates) — dollar estimates are unavailable and are not fabricated." />
                    </p>
                  )}
                </Card>

                {/* Hot → Cool */}
                <Card padding="none">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Hot → Cool tier candidates</h3>
                      <InfoTip title="Hot-tier accounts that hold backups (by binding evidence). Backups are written once and rarely read, so the Cool tier roughly halves their storage rate. Estimated saving = used GB × (Hot − Cool rate)." />
                    </div>
                    {data.optimization.hotToCool.totalMonthlySavingUsd != null && data.optimization.hotToCool.candidates.length > 0 && (
                      <Badge variant="success">Est. {fmtUsd(data.optimization.hotToCool.totalMonthlySavingUsd)} / mo</Badge>
                    )}
                  </div>
                  <p className="px-4 text-xs text-[var(--color-text-secondary)]">Backup-purpose Hot accounts · move to Cool for write-once / read-rarely data</p>
                  {data.optimization.hotToCool.candidates.length === 0 ? (
                    <div className="px-4 pb-4">
                      <NotCollected reason="No Hot-tier accounts with backup-binding evidence — nothing to move to Cool" />
                    </div>
                  ) : (
                    <DataTable columns={hotCandidateColumns} rows={data.optimization.hotToCool.candidates} rowKey={(r) => r.accountId} pageSize={10} emptyMessage="No candidates" />
                  )}
                </Card>

                {/* Replication migration cost/benefit */}
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <Card padding="none">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Over-provisioned replication</h3>
                        <InfoTip title="Fleet (non-backup) accounts on a geo-redundant SKU (GRS / RA-GRS). Dropping to LRS saves ~50–60% of the data-at-rest cost but removes cross-region durability — only for non-critical / scratch data." />
                      </div>
                      {data.optimization.overProvisioned.totalMonthlySavingUsd != null && data.optimization.overProvisioned.rows.length > 0 && (
                        <Badge variant="success">Est. {fmtUsd(data.optimization.overProvisioned.totalMonthlySavingUsd)} / mo</Badge>
                      )}
                    </div>
                    <p className="px-4 text-xs text-[var(--color-text-secondary)]">Saving of dropping to LRS · trade-off: loses geo-redundancy</p>
                    {data.optimization.overProvisioned.rows.length === 0 ? (
                      <div className="px-4 pb-4">
                        <NotCollected reason="No fleet accounts on a geo-redundant SKU" />
                      </div>
                    ) : (
                      <DataTable
                        columns={replChangeColumns('Est. saving / mo', 'text-[var(--color-status-success)]')}
                        rows={data.optimization.overProvisioned.rows}
                        rowKey={(r) => r.accountId}
                        pageSize={10}
                        emptyMessage="None"
                      />
                    )}
                  </Card>

                  <Card padding="none">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-[var(--color-text-primary)]">Under-protected backups</h3>
                        <InfoTip title="Backup-holding accounts on LRS (single-region, no geo-DR). Upgrading to GRS adds an async copy in a paired region for disaster recovery — roughly doubles the storage rate." />
                      </div>
                      {data.optimization.underProtected.totalMonthlyUpliftUsd != null && data.optimization.underProtected.rows.length > 0 && (
                        <Badge variant="warning">+{fmtUsd(data.optimization.underProtected.totalMonthlyUpliftUsd)} / mo for geo-DR</Badge>
                      )}
                    </div>
                    <p className="px-4 text-xs text-[var(--color-text-secondary)]">Cost of moving to GRS · trade-off: adds regional disaster recovery</p>
                    {data.optimization.underProtected.rows.length === 0 ? (
                      <div className="px-4 pb-4">
                        <NotCollected reason="No backup-holding accounts on LRS — all backup storage is already geo-redundant" />
                      </div>
                    ) : (
                      <DataTable
                        columns={replChangeColumns('Est. uplift / mo', 'text-[var(--color-status-warning)]')}
                        rows={data.optimization.underProtected.rows}
                        rowKey={(r) => r.accountId}
                        pageSize={10}
                        emptyMessage="None"
                      />
                    )}
                  </Card>
                </div>
              </div>
              )}
            </div>
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default StorageInsightsPage;
